/**
 * 프로젝트 상태 관리 스토어
 * 프로젝트 목록 조회, 생성, 삭제, 이름 변경, 복제 등
 */
import { create } from 'zustand'
import type { ProjectListItem, ProjectType } from '@/types'
import api, { ApiError } from '@/lib/api'

/** 백엔드 프로젝트 응답 타입 (title 필드 사용) */
interface BackendProject {
  id: string
  title: string
  thumbnail?: string | null
  type?: string
  settings?: unknown
  createdAt: string
  updatedAt: string
}

/** 백엔드 응답 → 프론트엔드 ProjectListItem 변환 */
function mapProject(p: BackendProject): ProjectListItem {
  return {
    id: p.id,
    name: p.title,
    thumbnailUrl: p.thumbnail ?? undefined,
    duration: 0,
    type: (p.type as ProjectType) || 'standard',
    createdAt: p.createdAt,
    updatedAt: p.updatedAt,
  }
}

interface ProjectState {
  /** 프로젝트 목록 */
  projects: ProjectListItem[]
  /** 로딩 상태 */
  isLoading: boolean
  /** 에러 메시지 */
  error: string | null
  /** 현재 페이지 */
  currentPage: number
  /** 전체 페이지 수 */
  totalPages: number
  /** 검색어 */
  searchQuery: string
  /** 정렬 기준 */
  sortBy: 'updatedAt' | 'createdAt' | 'name'
  /** 정렬 순서 */
  sortOrder: 'asc' | 'desc'
  /** 프로젝트 타입 필터 */
  typeFilter: ProjectType | 'all'
}

interface ProjectActions {
  /** 프로젝트 목록 조회 */
  fetchProjects: () => Promise<void>
  /** 새 프로젝트 생성 */
  createProject: (name: string, description?: string) => Promise<string>
  /** 프로젝트 삭제 */
  deleteProject: (projectId: string) => Promise<void>
  /** 프로젝트 이름 변경 */
  renameProject: (projectId: string, name: string) => Promise<void>
  /** 프로젝트 복제 */
  duplicateProject: (projectId: string) => Promise<void>
  /** 검색어 설정 */
  setSearchQuery: (query: string) => void
  /** 정렬 기준 설정 */
  setSortBy: (sortBy: ProjectState['sortBy']) => void
  /** 정렬 순서 설정 */
  setSortOrder: (sortOrder: ProjectState['sortOrder']) => void
  /** 현재 페이지 설정 */
  setCurrentPage: (page: number) => void
  /** 프로젝트 타입 필터 설정 */
  setTypeFilter: (type: ProjectType | 'all') => void
  /** 에러 설정 */
  setError: (error: string | null) => void
}

type ProjectStore = ProjectState & ProjectActions

/** 프론트엔드 sortBy → 백엔드 sort 파라미터 변환 */
function toBackendSort(sortBy: string, sortOrder: string): string {
  if (sortBy === 'createdAt') return sortOrder === 'asc' ? 'oldest' : 'newest'
  if (sortBy === 'name') return 'title'
  return sortOrder === 'asc' ? 'oldest' : 'newest'
}

export const useProjectStore = create<ProjectStore>()((set, get) => ({
  // 초기 상태
  projects: [],
  isLoading: false,
  error: null,
  currentPage: 1,
  totalPages: 1,
  searchQuery: '',
  sortBy: 'updatedAt',
  sortOrder: 'desc',
  typeFilter: 'all',

  // 프로젝트 목록 조회
  fetchProjects: async () => {
    set({ isLoading: true, error: null })
    try {
      const { searchQuery, sortBy, sortOrder, currentPage, typeFilter } = get()
      const params = new URLSearchParams({
        page: String(currentPage),
        limit: '12',
        sort: toBackendSort(sortBy, sortOrder),
      })
      if (searchQuery) {
        params.set('search', searchQuery)
      }
      if (typeFilter !== 'all') {
        params.set('type', typeFilter)
      }

      const response = await api.get<{
        projects: BackendProject[]
        pagination: { page: number; limit: number; total: number; totalPages: number }
      }>(`/projects?${params.toString()}`)

      set({
        projects: (response.projects ?? []).map(mapProject),
        totalPages: response.pagination?.totalPages ?? 1,
        isLoading: false,
      })
    } catch (error) {
      const message =
        error instanceof ApiError
          ? error.message
          : '프로젝트 목록을 불러오는데 실패했습니다.'
      set({
        isLoading: false,
        error: message,
      })
    }
  },

  // 새 프로젝트 생성
  createProject: async (name: string, _description?: string) => {
    set({ isLoading: true, error: null })
    try {
      const response = await api.post<{ message: string; project: BackendProject }>('/projects', {
        title: name,
        settings: { resolution: '1920x1080', fps: 30, aspectRatio: '16:9' },
      })

      const newProject = mapProject(response.project)

      set((state) => ({
        projects: [newProject, ...state.projects],
        isLoading: false,
      }))

      return newProject.id
    } catch (error) {
      const message =
        error instanceof ApiError
          ? error.message
          : '프로젝트 생성에 실패했습니다.'
      set({
        isLoading: false,
        error: message,
      })
      throw error
    }
  },

  // 프로젝트 삭제
  deleteProject: async (projectId: string) => {
    try {
      await api.del(`/projects/${projectId}`)
      set((state) => ({
        projects: state.projects.filter((p) => p.id !== projectId),
      }))
    } catch (error) {
      const message =
        error instanceof ApiError
          ? error.message
          : '프로젝트 삭제에 실패했습니다.'
      set({ error: message })
    }
  },

  // 프로젝트 이름 변경 (백엔드는 PUT + title 필드 사용)
  renameProject: async (projectId: string, name: string) => {
    try {
      await api.put(`/projects/${projectId}`, { title: name })
      set((state) => ({
        projects: state.projects.map((p) =>
          p.id === projectId
            ? { ...p, name, updatedAt: new Date().toISOString() }
            : p
        ),
      }))
    } catch (error) {
      const message =
        error instanceof ApiError
          ? error.message
          : '프로젝트 이름 변경에 실패했습니다.'
      set({ error: message })
    }
  },

  // 프로젝트 복제
  duplicateProject: async (projectId: string) => {
    try {
      const response = await api.post<{ message: string; project: BackendProject }>(
        `/projects/${projectId}/duplicate`
      )

      set((state) => ({
        projects: [mapProject(response.project), ...state.projects],
      }))
    } catch (error) {
      const message =
        error instanceof ApiError
          ? error.message
          : '프로젝트 복제에 실패했습니다.'
      set({ error: message })
    }
  },

  // 검색어 설정 (페이지 초기화)
  setSearchQuery: (query) => set({ searchQuery: query, currentPage: 1 }),
  // 정렬 기준 설정 (페이지 초기화)
  setSortBy: (sortBy) => set({ sortBy, currentPage: 1 }),
  // 정렬 순서 설정 (페이지 초기화)
  setSortOrder: (sortOrder) => set({ sortOrder, currentPage: 1 }),
  // 프로젝트 타입 필터 설정 (페이지 초기화)
  setTypeFilter: (typeFilter) => set({ typeFilter, currentPage: 1 }),
  // 현재 페이지 설정
  setCurrentPage: (page) => set({ currentPage: page }),
  // 에러 설정
  setError: (error) => set({ error }),

  // 클라우드 동기화 (모바일 sync API 연동)
  syncFromCloud: async () => {
    try {
      const lastSync = localStorage.getItem('video-editor-last-sync')
      const response = await api.post<{
        projects: BackendProject[]
        syncedAt: string
      }>('/mobile/sync', { lastSyncAt: lastSync })

      if (response.projects && response.projects.length > 0) {
        const syncedProjects = response.projects.map(mapProject)
        set((state) => {
          const merged = [...state.projects]
          for (const sp of syncedProjects) {
            const idx = merged.findIndex((p) => p.id === sp.id)
            if (idx >= 0) {
              merged[idx] = sp
            } else {
              merged.unshift(sp)
            }
          }
          return { projects: merged }
        })
      }

      if (response.syncedAt) {
        localStorage.setItem('video-editor-last-sync', response.syncedAt)
      }
    } catch {
      // 동기화 실패 시 무시 (다음 주기에 재시도)
    }
  },
}))
